import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Moment from 'moment';

import type {
  CanvassStatusOption,
  DoorCanvass,
  DoorCanvassPass,
  DoorCanvassPassAggData,
} from '@/types/doorcanvass';
import type { CutableFieldOption } from '@/types/turfcutter';

const DISPLAY_DATE = 'MMM D, YYYY h:mm A';

export interface DoorCanvassDetailLookups {
  officeName?: string | null;
  scriptName?: string | null;
}

export interface DoorCanvassDetailProps {
  canvass: DoorCanvass;
  passes: DoorCanvassPass[];
  lookups?: DoorCanvassDetailLookups;
  /** When true, hides the page title row (e.g. embedded in another layout). */
  embedded?: boolean;
}

function SectionHeader({
  iconClass,
  title,
}: {
  iconClass: string;
  title: string;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <i className={iconClass} aria-hidden style={{ opacity: 0.85 }} />
      <Typography variant="h6" component="h2">
        {title}
      </Typography>
    </Stack>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Grid container spacing={1} sx={{ py: 0.75 }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body1" component="div">
          {children}
        </Typography>
      </Grid>
    </Grid>
  );
}

function BoolChip({ value, trueLabel, falseLabel }: { value: boolean; trueLabel: string; falseLabel: string }) {
  return (
    <Chip
      size="small"
      label={value ? trueLabel : falseLabel}
      color={value ? 'success' : 'default'}
      variant={value ? 'filled' : 'outlined'}
    />
  );
}

function formatAggMetric(value: number | undefined): string {
  if (value === undefined || value === null) return '—';
  return String(value);
}

function PassAggSummary({ agg }: { agg: DoorCanvassPassAggData | null }) {
  if (!agg || Object.keys(agg).length === 0) {
    return <Typography variant="body2" color="text.secondary">No aggregate data yet</Typography>;
  }

  const rows: { label: string; key: keyof DoorCanvassPassAggData }[] = [
    { label: 'Attempted', key: 'attempted' },
    { label: 'Walkable cut', key: 'walkable_cut' },
    { label: 'Unwalkable cut', key: 'unwalkable_cut' },
    { label: 'Walkable turfs', key: 'walkable_turfs' },
    { label: 'Unwalkable turfs', key: 'unwalkable_turfs' },
    { label: 'Regular doors', key: 'regular_doors_count' },
    { label: 'Driver doors', key: 'driver_doors_count' },
    { label: 'Training doors', key: 'training_doors_count' },
    { label: 'Gated community', key: 'gated_community_doors_count' },
    { label: 'Controlled access', key: 'controlled_access_doors_count' },
    { label: 'Unsafe', key: 'unsafe_doors_count' },
    { label: 'Non-contiguous', key: 'non_contiguious_doors_count' },
    { label: 'Needs scouting', key: 'needs_scouting_doors_count' },
  ];

  return (
    <Stack spacing={0.5}>
      {rows.map(({ label, key }) =>
        agg[key] !== undefined ? (
          <Typography key={key} variant="body2">
            <strong>{label}:</strong> {formatAggMetric(agg[key])}
          </Typography>
        ) : null
      )}
    </Stack>
  );
}

function StatusChips({ statuses }: { statuses: CanvassStatusOption[] }) {
  if (!statuses?.length) {
    return <Typography variant="body2" color="text.secondary">No statuses configured</Typography>;
  }

  const sorted = [...statuses].sort(
    (a, b) => (a.heirarchy_index ?? 0) - (b.heirarchy_index ?? 0)
  );

  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {sorted.map((s) => (
        <Chip
          key={s.id}
          size="small"
          label={s.display_text || s.status}
          sx={{
            bgcolor: s.color || undefined,
            color: s.color ? '#fff' : undefined,
          }}
          title={s.status}
        />
      ))}
    </Stack>
  );
}

function CutableFieldsTable({ fields }: { fields: CutableFieldOption[] }) {
  if (!fields.length) {
    return <Typography variant="body2" color="text.secondary">No cutable fields in cut config</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Display</TableCell>
            <TableCell>Type (district field)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f) => (
            <TableRow key={`${f.type}-${f.display}`}>
              <TableCell>{f.display}</TableCell>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">{f.type}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Read-only door canvass detail layout. Use as the main body of
 * `/doorcanvass/:doorcanvass_id` or embed elsewhere with `embedded`.
 */
export default function DoorCanvassDetail({
  canvass,
  passes,
  lookups = {},
  embedded = false,
}: DoorCanvassDetailProps) {
  const cutableFields = canvass.cut_config?.cutable_fields ?? [];
  const statuses = canvass.statuses ?? [];
  const sortedPasses = [...passes].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const scriptLink =
    canvass.script != null ? `/script/${canvass.script}/edit` : null;

  return (
    <Box sx={{ maxWidth: 960, mx: embedded ? 0 : 'auto', width: '100%' }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            {canvass.name}
          </Typography>
          <Button
            component={Link}
            to="/doorcanvass"
            variant="outlined"
            size="small"
            startIcon={<i className="fas fa-arrow-left" aria-hidden />}
          >
            All door canvasses
          </Button>
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <SectionHeader iconClass="fas fa-link" title="Quick links" />
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            component={Link}
            to={`/doorcanvass/${canvass.id}/schedule`}
            variant="contained"
            size="small"
            startIcon={<i className="fas fa-clock" aria-hidden />}
          >
            Schedule
          </Button>
          {scriptLink && (
            <Button
              component={Link}
              to={scriptLink}
              variant="outlined"
              size="small"
              startIcon={<i className="fas fa-file-alt" aria-hidden />}
            >
              Script
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <SectionHeader iconClass="fas fa-info-circle" title="Overview" />
        <DetailRow label="ID">{canvass.id}</DetailRow>
        <Divider sx={{ my: 1 }} />
        <DetailRow label="Campaign ID">{canvass.campaign}</DetailRow>
        <DetailRow label="Office">
          {lookups.officeName ?? (canvass.office != null ? `#${canvass.office}` : '—')}
        </DetailRow>
        <DetailRow label="Script">
          {scriptLink ? (
            <Link to={scriptLink}>{lookups.scriptName ?? `Script #${canvass.script}`}</Link>
          ) : (
            lookups.scriptName ?? '—'
          )}
        </DetailRow>
        <DetailRow label="Created">
          {canvass.created_at
            ? Moment(canvass.created_at).format(DISPLAY_DATE)
            : '—'}
        </DetailRow>
        <DetailRow label="Created by user ID">{canvass.created_by ?? '—'}</DetailRow>
        <DetailRow label="Active">
          <BoolChip value={canvass.is_active} trueLabel="Active" falseLabel="Inactive" />
        </DetailRow>
        <DetailRow label="Uber rides">
          <BoolChip value={canvass.allow_uber} trueLabel="Allowed" falseLabel="Not allowed" />
        </DetailRow>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <SectionHeader iconClass="fas fa-cut" title="Turf cutting (cut config)" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          District types available in Turf Cutter for passes under this canvass.
        </Typography>
        <CutableFieldsTable fields={cutableFields} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <SectionHeader iconClass="fas fa-list" title="Canvass statuses" />
        <StatusChips statuses={statuses} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <SectionHeader iconClass="fas fa-layer-group" title="Passes" />
        {!sortedPasses.length ? (
          <Typography variant="body2" color="text.secondary">No passes for this canvass</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Households</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Locked</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPasses.map((pass) => (
                  <TableRow key={pass.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{pass.name}</Typography>
                      {pass.filter_info ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {pass.filter_info}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{pass.household_count ?? '—'}</TableCell>
                    <TableCell>
                      <BoolChip
                        value={pass.is_active}
                        trueLabel="Yes"
                        falseLabel="No"
                      />
                    </TableCell>
                    <TableCell>
                      {pass.is_locked ? (
                        <Chip size="small" color="error" label="Locked" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {pass.created_at
                        ? Moment(pass.created_at).format('M/D/YY')
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {pass.is_locked ? (
                        <Typography variant="caption" className="danger-text">
                          Turf cutter locked
                        </Typography>
                      ) : (
                        <Button
                          component={Link}
                          to={`/turfcutter/${pass.id}`}
                          size="small"
                          startIcon={<i className="fa fa-cut" aria-hidden />}
                        >
                          Turf cutter
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {sortedPasses.some((p) => p.agg_data && Object.keys(p.agg_data).length > 0) && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <SectionHeader iconClass="fas fa-chart-bar" title="Pass aggregates" />
          <Stack spacing={3}>
            {sortedPasses.map((pass) => (
              <Box key={`agg-${pass.id}`}>
                <Typography variant="subtitle2" gutterBottom>
                  {pass.name}
                </Typography>
                <PassAggSummary agg={pass.agg_data} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
